import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import ThemeToggle from './ThemeToggle';
import AppImage from '../AppImage';
import Select from './Select';
import { useAuth } from '../../contexts/AuthContext';

const Header = () => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [language, setLanguage] = useState('pt');
  const location = useLocation();
  const navigate = useNavigate();
  const { session, user, logout } = useAuth();

  // Refs para detectar cliques externos
  const languageDropdownRef = useRef(null);
  const userMenuRef = useRef(null);

  // Obter dados do usuário com fallbacks
  const userName = session?.systemUser?.name || user?.email || 'Usuário';
  const userRole = session?.userTenant?.role 
    ? session.userTenant.role.charAt(0).toUpperCase() + session.userTenant.role.slice(1)
    : 'Usuário';

  // Opções de idioma com bandeiras
  const languageOptions = [
    { value: 'pt', label: '🇧🇷 Português' },
    { value: 'en', label: '🇺🇸 English' },
    { value: 'es', label: '🇪🇸 Español' }
  ];

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Fechar dropdown de idiomas se clicar fora
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setIsLanguageOpen(false);
      }
      
      // Fechar menu do usuário se clicar fora
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    // Adicionar event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup: remover event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const primaryNavItems = [
    { label: 'Início', path: '/dashboard-home', icon: 'Home' },
    { label: 'Meu Progresso', path: '/user-progress-dashboard', icon: 'TrendingUp' },
    { label: 'Atividades', path: '/activity-interface', icon: 'BookOpen' },
    { label: 'Cursos', path: '/courses', icon: 'GraduationCap' },
    { label: 'Mapa de Metas', path: '/goals-map-interface', icon: 'Target' }
  ];

  const secondaryNavItems = [
    { label: 'Admin', path: '/admin-panel-dashboard', icon: 'Settings', roles: ['owner', 'admin', 'manager'] },
    { label: 'Configurações de Tema', path: '/theme-settings-panel', icon: 'Palette' },
/*   TODO: Add help and settings back later  { label: 'Ajuda', path: '/help', icon: 'HelpCircle' },
    { label: 'Configurações', path: '/settings', icon: 'Settings' }, */
    { label: 'Sair', action: 'logout', icon: 'LogOut' }
  ];

  const isActive = (path) => location?.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);
    setIsUserMenuOpen(false);
  };

  const handleLogout = async () => {
    if (!logout || typeof logout !== 'function') {
      console.error('❌ Função logout não disponível');
      navigate('/login-screen');
      return;
    }
    
    try {
      await logout();
      navigate('/login-screen');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login-screen');
    }
  };

  const handleMobileNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    navigate('/dashboard-home');
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    setIsLanguageOpen(false);
    // TODO: Implementar sistema de traduções
    console.log('Idioma alterado para:', newLanguage);
  };

  const handleLanguageToggle = () => {
    setIsLanguageOpen(!isLanguageOpen);
  };

  // Usar o hook useAuth para verificar roles
  const { hasRole } = useAuth();

  return (
    <header className="fixed top-0 left-0 right-0 z-100 bg-card border-b border-border theme-transition">
      <div className="flex items-center justify-between h-16 px-4 sm:px-8 md:px-12 lg:px-16 xl:px-20 2xl:px-24">
        {/* Logo */}
        <div 
          className="flex items-center cursor-pointer transition-micro"
          onClick={handleLogoClick}
        >
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-lg overflow-hidden cursor-pointer transition-transform duration-300 origin-top z-50 hover:scale-[4] hover:translate-y-8 md:hover:scale-[6] md:hover:translate-y-12">
              <AppImage
                src="/assets/images/major-high-logo.png"
                alt="Major High Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h1 className="text-lg md:text-xl font-heading font-semibold text-foreground truncate max-w-[200px] md:max-w-none">
                Major High
              </h1>
              <p className="hidden lg:block text-xs text-muted-foreground font-caption">Academy Platform</p>
              <p className="hidden md:block lg:hidden text-xs text-muted-foreground font-caption">Academy</p>
            </div>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-1">
          {primaryNavItems?.map((item) => (
            <Button
              key={item?.path}
              variant={isActive(item?.path) ? "default" : "ghost"}
              onClick={() => handleNavigation(item?.path)}
              iconName={item?.icon}
              iconPosition="left"
              iconSize={18}
              className="px-4 py-2"
            >
              {item?.label}
            </Button>
          ))}
        </nav>

        {/* Desktop User Menu & More */}
        <div className="hidden lg:flex items-center space-x-2">
          {/* Calendar Button */}
          <Button
            variant="ghost"
            onClick={() => handleNavigation('/calendar-main-interface')}
            iconName="Calendar"
            iconPosition="left"
            iconSize={18}
            className="px-4 py-2"
          >
            Calendário
          </Button>

          {/* Theme Toggle - Apenas para não-estudantes */}
          {/* {session?.userTenant?.role !== 'student' && <ThemeToggle />} */}

          {/* Language Dropdown - Substituindo o botão "Mais" */}
          <div className="relative" ref={languageDropdownRef}>
            <button
              onClick={handleLanguageToggle}
              className="flex h-10 w-36 items-center justify-between rounded-md border-2 border-primary px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-accent hover:text-accent-foreground transition-micro"
            >
              <span className="truncate">
                {languageOptions.find(opt => opt.value === language)?.label || '🇧🇷 Português'}
              </span>
              <Icon 
                name={isLanguageOpen ? "ChevronUp" : "ChevronDown"} 
                size={16} 
                className="text-muted-foreground transition-transform"
              />
            </button>

            {/* Language Dropdown Menu */}
            {isLanguageOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-popover border border-border rounded-lg shadow-tier-2 py-2 z-150 theme-transition language-dropdown">
                {languageOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleLanguageChange(option.value)}
                    className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground transition-micro"
                  >
                    <span className="text-lg">{option.label.split(' ')[0]}</span>
                    <span>{option.label.split(' ').slice(1).join(' ')}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* User Profile - Agora clicável com dropdown */}
          <div className="relative flex items-center space-x-2 sm:space-x-3 pl-2 sm:pl-4 border-l border-border" ref={userMenuRef}>
            <div 
              className="flex items-center space-x-2 sm:space-x-3 cursor-pointer p-2 rounded-lg hover:bg-accent hover:text-accent-foreground transition-micro"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            >
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-secondary rounded-full flex items-center justify-center">
                <Icon name="User" size={16} color="var(--color-secondary-foreground)" />
              </div>
              <div className="hidden lg:block">
                <p className="text-sm font-medium text-foreground">{userName}</p>
                <p className="text-xs text-muted-foreground font-caption">{userRole}</p>
              </div>
              <Icon 
                name={isUserMenuOpen ? "ChevronUp" : "ChevronDown"} 
                size={16} 
                className="text-muted-foreground"
              />
            </div>

            {/* User Dropdown Menu - Movido do botão "Mais" */}
            {isUserMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-40 sm:w-48 bg-popover border border-border rounded-lg shadow-tier-2 py-2 z-150 theme-transition">
                {secondaryNavItems?.map((item) => {
                  // Verificar se o item requer role específico
                  if (item?.roles && !hasRole(item.roles)) {
                    return null; // Não renderizar o item se o usuário não tiver o role necessário
                  }
                  
                  return (
                    <button
                      key={item?.path || item?.action}
                      onClick={() => {
                        if (item?.action === 'logout') {
                          handleLogout();
                        } else if (item?.path) {
                          handleNavigation(item?.path);
                        }
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground transition-micro"
                    >
                      <Icon name={item?.icon} size={16} />
                      <span>{item?.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden flex items-center space-x-2">
          {/* {session?.userTenant?.role !== 'student' && <ThemeToggle />} */}
          <Button
            variant="ghost"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            iconName={isMobileMenuOpen ? "X" : "Menu"}
            iconSize={24}
            className="p-2"
          />
        </div>
      </div>
      
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 bg-background z-150 theme-transition">
          <div className="p-6 space-y-4">
            {/* Primary Navigation */}
            <div className="space-y-2">
              {primaryNavItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleMobileNavigation(item?.path)}
                  className={`w-full flex items-center space-x-3 p-4 rounded-lg transition-micro ${
                    isActive(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card text-card-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span className="font-medium">{item?.label}</span>
                </button>
              ))}
            </div>

            {/* Calendar Button - Mobile */}
            <div className="space-y-2">
              <button
                onClick={() => handleMobileNavigation('/calendar-main-interface')}
                className="w-full flex items-center space-x-3 p-4 rounded-lg bg-card text-card-foreground hover:bg-accent hover:text-accent-foreground transition-micro"
              >
                <Icon name="Calendar" size={20} />
                <span className="font-medium">Calendário</span>
              </button>
            </div>

            {/* Divider */}
            <div className="border-t border-border my-6"></div>

            {/* Secondary Navigation */}
            <div className="space-y-2">
              {secondaryNavItems?.map((item) => {
                // Verificar se o item requer role específico
                if (item?.roles && !hasRole(item.roles)) {
                  return null; // Não renderizar o item se o usuário não tiver o role necessário
                }
                
                return (
                  <button
                    key={item?.path || item?.action}
                    onClick={() => {
                      if (item?.action === 'logout') {
                        handleLogout();
                      } else if (item?.path) {
                        handleMobileNavigation(item?.path);
                      }
                    }}
                    className="w-full flex items-center space-x-3 p-4 rounded-lg text-card-foreground hover:bg-accent hover:text-accent-foreground transition-micro"
                  >
                    <Icon name={item?.icon} size={20} />
                    <span className="font-medium">{item?.label}</span>
                  </button>
                );
              })}
            </div>

            {/* User Profile */}
            <div className="border-t border-border pt-6">
              <div className="flex items-center space-x-3 p-4 bg-card rounded-lg">
                <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center">
                  <Icon name="User" size={20} color="var(--color-secondary-foreground)" />
                </div>
                <div>
                  <p className="font-medium text-foreground">{userName}</p>
                  <p className="text-sm text-muted-foreground font-caption">{userRole}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
